import { memo } from 'react';

import type { MessagePart } from './message-part-utils';

import { ReasoningBlock } from './message-blocks/reasoning-block';
import { renderDynamicToolPart, renderStaticToolPart } from './message-tool-renderers';

import { ChatTextMarkdown } from '@/components/chat/chat-text-markdown';

type MessageTextPartProps = {
  messageId: string;
  index: number;
  text: string;
};

export const MessageTextPart = memo(
  ({ text }: MessageTextPartProps) => (
    <div className="flex min-w-0 flex-col gap-2">
      <ChatTextMarkdown>{text}</ChatTextMarkdown>
    </div>
  ),
  (prevProps, nextProps) =>
    prevProps.messageId === nextProps.messageId &&
    prevProps.index === nextProps.index &&
    prevProps.text === nextProps.text,
);

MessageTextPart.displayName = 'MessageTextPart';

type MessageReasoningPartProps = {
  messageId: string;
  index: number;
  text: string;
  defaultOpen: boolean;
  autoCollapse: boolean;
  isStreaming: boolean;
  className?: string;
};

export const MessageReasoningPart = memo(
  ({ text, defaultOpen, autoCollapse, isStreaming, className }: MessageReasoningPartProps) => (
    <ReasoningBlock
      className={className}
      text={text}
      defaultOpen={defaultOpen}
      autoCollapse={autoCollapse}
      isStreaming={isStreaming}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.messageId === nextProps.messageId &&
    prevProps.index === nextProps.index &&
    prevProps.text === nextProps.text &&
    prevProps.defaultOpen === nextProps.defaultOpen &&
    prevProps.autoCollapse === nextProps.autoCollapse &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.className === nextProps.className,
);

MessageReasoningPart.displayName = 'MessageReasoningPart';

type MemoizedToolPartProps = {
  messageId: string;
  part: MessagePart;
  index: number;
  partSignature: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  toolClassName?: string;
  handleAiPrComment: (toolCallId?: string) => void;
};

export const MemoizedDynamicToolPart = memo(
  ({ part, index, open, onOpenChange, toolClassName }: MemoizedToolPartProps) =>
    renderDynamicToolPart(part, index, toolClassName, () => ({ open, onOpenChange })),
  (prevProps, nextProps) =>
    prevProps.messageId === nextProps.messageId &&
    prevProps.partSignature === nextProps.partSignature &&
    prevProps.open === nextProps.open &&
    prevProps.toolClassName === nextProps.toolClassName,
);

MemoizedDynamicToolPart.displayName = 'MemoizedDynamicToolPart';

export const MemoizedStaticToolPart = memo(
  ({
    messageId,
    part,
    index,
    open,
    onOpenChange,
    toolClassName,
    handleAiPrComment,
  }: MemoizedToolPartProps) =>
    renderStaticToolPart(part, index, {
      messageId,
      handleAiPrComment,
      getToolProps: () => ({ open, onOpenChange }),
      toolClassName,
    }),
  (prevProps, nextProps) =>
    prevProps.messageId === nextProps.messageId &&
    prevProps.partSignature === nextProps.partSignature &&
    prevProps.open === nextProps.open &&
    prevProps.toolClassName === nextProps.toolClassName,
);

MemoizedStaticToolPart.displayName = 'MemoizedStaticToolPart';
